import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocale } from '../i18n'
import { getBillingRates, updateBillingRate, importBillingRates } from '../api/client'

export default function BillingRates() {
  const { t } = useTranslation(['purchases', 'common'])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // { id, field }
  const [editVal, setEditVal] = useState('')
  const [importResult, setImportResult] = useState(null)
  const [importing, setImporting] = useState(false)
  const fileRef = useRef()

  const load = async () => {
    setLoading(true)
    try { setRows(await getBillingRates()) } catch { setRows([]) }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const startEdit = (id, field, current) => {
    setEditing({ id, field })
    setEditVal(current == null ? '' : String(current))
  }

  const commitEdit = async () => {
    if (!editing) return
    const row = rows.find(r => r.team_member_id === editing.id)
    if (!row) { setEditing(null); setEditVal(''); return }

    let nextRate = row.rate_usd
    if (editing.field === 'rate_usd') {
      const trimmed = editVal.trim()
      if (trimmed === '') {
        // user cleared the field and blurred — treat as cancel, don't overwrite with 0
        setEditing(null); setEditVal(''); return
      }
      const parsed = parseFloat(trimmed)
      if (!Number.isFinite(parsed) || parsed < 0) {
        // invalid input — keep editor open so the user can correct it
        return
      }
      nextRate = parsed
    }

    const payload = {
      role: editing.field === 'role' ? (editVal.trim() || null) : (row.role || null),
      rate_usd: nextRate,
    }
    try {
      const updated = await updateBillingRate(editing.id, payload)
      setRows(prev => prev.map(r => r.team_member_id === updated.team_member_id ? updated : r))
    } catch (err) {
      // surface a small inline message; don't lose the user's edit
      console.error('Failed to save rate', err)
      window.alert(t('billingRates.failedSave') + ' ' + (err?.response?.data?.detail || t('billingReport.tryAgain')))
      return
    }
    setEditing(null)
    setEditVal('')
  }

  const handleKey = (e) => {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') { setEditing(null); setEditVal('') }
  }

  const handleImport = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setImporting(true)
    setImportResult(null)
    try {
      const result = await importBillingRates(file)
      setImportResult(result)
      await load()
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || 'Import failed' })
    }
    setImporting(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  const fmtDate = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return d.toLocaleDateString(currentLocale(), { year: 'numeric', month: 'short', day: 'numeric' })
  }

  return (
    <div className="px-8 pb-8 pt-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('billingRates.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('billingRates.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleImport}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {importing ? t('billingRates.importing') : t('billingRates.importExcel')}
          </button>
          <button
            onClick={load}
            className="border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {t('billingRates.refresh')}
          </button>
        </div>
      </div>

      {importResult && (
        <div className="mb-4">
          {importResult.error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{importResult.error}</div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-xl px-4 py-3 text-sm">
              {t('billingRates.importSuccess', { count: importResult.matched })}
              {importResult.skipped?.length > 0 && (
                <>
                  {' '}{t('billingRates.skipped', { count: importResult.skipped.length })}{' '}
                  {importResult.skipped.map((s, i) => (
                    <span key={i} className="inline-block bg-white border border-emerald-200 rounded px-1.5 py-0.5 mr-1 text-xs text-emerald-700">
                      {s.name} <span className="opacity-60">({s.reason})</span>
                    </span>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm">{t('common:actions.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">{t('billingRates.noTeamMembers')}</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                <th className="text-left font-semibold px-4 py-3 w-[28%]">{t('billingRates.cols.engineer')}</th>
                <th className="text-left font-semibold px-4 py-3">{t('billingRates.cols.role')}</th>
                <th className="text-right font-semibold px-4 py-3 w-32">{t('billingRates.cols.rateUsd')}</th>
                <th className="text-right font-semibold px-4 py-3 w-40">{t('billingRates.cols.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isEditingRole = editing?.id === r.team_member_id && editing.field === 'role'
                const isEditingRate = editing?.id === r.team_member_id && editing.field === 'rate_usd'
                const noRate = r.rate_usd === 0 && !r.updated_at
                return (
                  <tr key={r.team_member_id} className={`border-t border-gray-100 ${noRate ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-4 py-2 font-medium text-gray-800">{r.name}</td>
                    <td className="px-4 py-2 text-gray-700">
                      {isEditingRole ? (
                        <input
                          autoFocus
                          type="text"
                          value={editVal}
                          onChange={e => setEditVal(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={handleKey}
                          className="w-full border border-gray-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                        />
                      ) : (
                        <button
                          onClick={() => startEdit(r.team_member_id, 'role', r.role)}
                          className="text-left hover:bg-gray-100 rounded px-2 py-1 -mx-2 -my-1 w-full"
                        >
                          {r.role || <span className="text-gray-300">—</span>}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {isEditingRate ? (
                        <input
                          autoFocus
                          type="number"
                          step="0.01"
                          min="0"
                          value={editVal}
                          onChange={e => setEditVal(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={handleKey}
                          className="w-24 text-right border border-gray-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                        />
                      ) : (
                        <button
                          onClick={() => startEdit(r.team_member_id, 'rate_usd', r.rate_usd)}
                          className="hover:bg-gray-100 rounded px-2 py-1 -mx-2 -my-1"
                        >
                          {noRate ? <span className="text-amber-600">{t('billingRates.addRate')}</span> : `$${r.rate_usd}`}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-gray-400">{fmtDate(r.updated_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
